/**
 * ShapeToolbar — Tabbed toolbar: Design, Transitions, Animations
 * Includes: Insert shapes, Layouts, Themes, Background, Alignment Tools, Rotation, Slide Numbers
 *
 * Alignment logic is in alignmentUtils.js
 * Theme definitions are in src/constants/themes.js
 */

import { useState, useRef } from 'react';
import { SLIDE_W, SLIDE_H } from './SlideCanvas';
import { LAYOUTS } from './slideTemplates';
import { THEMES } from '../../../constants/themes';
import { alignShapes } from './alignmentUtils';

const FILL_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#1F2937', '#FFFFFF', '#F3F4F6', '#FDE68A', '#D1FAE5', '#DBEAFE', '#FCE7F3', 'transparent'];
const STROKE_COLORS = ['#000000', '#374151', '#6B7280', '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', 'transparent'];
const FONT_SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72, 96];
const ANIMATION_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'slideInLeft', label: 'Slide from Left' },
  { value: 'slideInRight', label: 'Slide from Right' },
  { value: 'slideInUp', label: 'Slide from Bottom' },
  { value: 'slideInDown', label: 'Slide from Top' },
  { value: 'scaleIn', label: 'Scale In' },
  { value: 'bounceIn', label: 'Bounce In' },
];

// ─── Align button ─────────────────────────────────────────────────────────────
function AlignBtn({ title, onClick, children, disabled }) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 rounded border border-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

export default function ShapeToolbar({
  selectedShape,
  selectedShapes,   // array of all currently-selected shapes (for multi-select alignment)
  onAdd,
  onUpdate,
  onDelete,
  currentSlide,
  slides,
  onSlideUpdate,
  allShapes,
  onApplyLayout,
  onApplyTheme,
  showSlideNumbers,     // boolean — whether slide numbers are shown
  onToggleSlideNumbers, // () => void — toggle the flag
}) {
  const [tab, setTab] = useState('design');
  const [showLayouts, setShowLayouts] = useState(false);
  const [showThemes, setShowThemes] = useState(false);
  const fileRef = useRef(null);

  const multiSelected = selectedShapes && selectedShapes.length >= 2;
  const canDistribute = selectedShapes && selectedShapes.length >= 3;

  const addShape = (type, defaults = {}) => {
    onAdd({
      id: `shape_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      x: SLIDE_W / 2 - 100 + Math.random() * 50,
      y: SLIDE_H / 2 - 50 + Math.random() * 50,
      width: type === 'text' ? 300 : type === 'line' ? 200 : 200,
      height: type === 'text' ? 60 : type === 'line' ? 4 : 120,
      fill: type === 'text' ? 'transparent' : type === 'line' ? '#374151' : '#3B82F6',
      stroke: '',
      strokeWidth: 0,
      radius: type === 'ellipse' ? 0 : 8,
      text: type === 'text' ? 'Text' : '',
      textColor: type === 'text' ? '#1F2937' : '#FFFFFF',
      fontSize: type === 'text' ? 24 : 16,
      fontWeight: 'normal',
      textAlign: 'center',
      opacity: 1,
      rotation: 0,
      zIndex: Date.now(),
      animation: 'none',
      animationDelay: 0,
      animationDuration: 500,
      ...defaults,
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/work/documents/upload-image', { method: 'POST', credentials: 'include', body: form });
      const data = await res.json();
      if (data.storageKey) {
        addShape('image', { storageKey: data.storageKey, fill: 'transparent', width: 300, height: 200 });
      }
    } catch {
      const url = URL.createObjectURL(file);
      addShape('image', { src: url, fill: 'transparent', width: 300, height: 200 });
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  // Convenience wrapper so call-sites don't need to pass onUpdate
  const align = (mode) => alignShapes(
    multiSelected ? selectedShapes : (selectedShape ? [selectedShape] : []),
    mode,
    onUpdate
  );

  const s = selectedShape; // shorthand
  const rotation = s ? (s.rotation || 0) : 0;

  return (
    <div className="bg-white border-b border-gray-200 flex-shrink-0">
      {/* Tab bar */}
      <div className="flex items-center gap-0 px-3 border-b border-gray-100">
        {['design', 'transitions', 'animations'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors border-b-2 ${
              tab === t ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}>
            {t}
          </button>
        ))}
        <div className="flex-1" />

        {/* Position / size / rotation readout — always visible when a shape is selected */}
        {s && (
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            {/* Position */}
            <span title="X, Y position">
              <span className="font-semibold text-gray-500">X</span>{' '}{Math.round(s.x)}
              <span className="mx-1 text-gray-300">·</span>
              <span className="font-semibold text-gray-500">Y</span>{' '}{Math.round(s.y)}
            </span>

            {/* Size */}
            <span title="Width × Height">
              <span className="font-semibold text-gray-500">W</span>{' '}{Math.round(s.width)}
              <span className="mx-1 text-gray-300">×</span>
              <span className="font-semibold text-gray-500">H</span>{' '}{Math.round(s.height)}
            </span>

            {/* Rotation — always shown, highlighted when non-zero */}
            <span
              title="Rotation in degrees"
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${
                rotation !== 0
                  ? 'bg-indigo-50 text-indigo-600 font-semibold'
                  : 'text-gray-400'
              }`}
            >
              {/* Rotation icon */}
              <svg
                viewBox="0 0 16 16"
                className={`w-3 h-3 flex-shrink-0 ${
                  rotation !== 0 ? 'text-indigo-500' : 'text-gray-400'
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path d="M13 3.5A6.5 6.5 0 1 1 8 1.5" strokeLinecap="round" />
                <path d="M13 1v3h-3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{rotation}&deg;</span>
            </span>
          </div>
        )}
      </div>

      {/* Tab content */}
      <div className="px-3 py-2 min-h-[44px] overflow-x-auto">

        {/* ═══ DESIGN TAB — no shape selected ═══ */}
        {tab === 'design' && !s && (
          <div className="flex items-center gap-2 flex-wrap">

            {/* Insert shapes */}
            <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mr-1">Insert</span>
            <button onClick={() => addShape('rect')} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="3" y="5" width="18" height="14" rx="2" /></svg>
              Rectangle
            </button>
            <button onClick={() => addShape('rect', { radius: 20 })} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="3" y="5" width="18" height="14" rx="7" /></svg>
              Rounded
            </button>
            <button onClick={() => addShape('ellipse')} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><ellipse cx="12" cy="12" rx="10" ry="8" /></svg>
              Ellipse
            </button>
            <button onClick={() => addShape('rect', { width: 200, height: 4, radius: 2, fill: '#374151' })} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
              <svg className="w-4 h-4" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><line x1="3" y1="12" x2="21" y2="12" /></svg>
              Line
            </button>
            <button onClick={() => addShape('text')} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
              <span className="text-sm font-bold w-4 text-center">T</span>
              Text
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
              Image
            </button>

            <div className="w-px h-5 bg-gray-200 mx-1" />

            {/* Layouts picker */}
            <div className="relative">
              <button
                onClick={() => { setShowLayouts(v => !v); setShowThemes(false); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  showLayouts ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                  <rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" />
                  <rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" />
                </svg>
                Layouts
                <svg className={`w-3 h-3 transition-transform ${showLayouts ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M6 9l6 6 6-6" /></svg>
              </button>

              {showLayouts && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-[420px]">
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Choose a Layout</div>
                  <div className="grid grid-cols-3 gap-2">
                    {LAYOUTS.map(layout => (
                      <button
                        key={layout.id}
                        onClick={() => {
                          onApplyLayout?.(layout);
                          setShowLayouts(false);
                        }}
                        className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors group text-left"
                      >
                        <div className="w-full aspect-[16/9] bg-gray-100 rounded border border-gray-200 relative overflow-hidden group-hover:border-indigo-300">
                          <LayoutPreview layout={layout} />
                        </div>
                        <div className="text-[10px] font-semibold text-gray-700 text-center leading-tight">{layout.label}</div>
                        <div className="text-[9px] text-gray-400 text-center leading-tight">{layout.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Themes picker */}
            <div className="relative">
              <button
                onClick={() => { setShowThemes(v => !v); setShowLayouts(false); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  showThemes ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 3a9 9 0 010 18" fill="currentColor" opacity="0.15" />
                  <circle cx="12" cy="12" r="3" fill="currentColor" />
                </svg>
                Themes
                <svg className={`w-3 h-3 transition-transform ${showThemes ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M6 9l6 6 6-6" /></svg>
              </button>

              {showThemes && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-80">
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Apply Theme to All Slides</div>
                  <div className="grid grid-cols-2 gap-2">
                    {THEMES.map(theme => (
                      <button
                        key={theme.id}
                        onClick={() => {
                          onApplyTheme?.(theme);
                          setShowThemes(false);
                        }}
                        className="flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                      >
                        <div className="flex gap-0.5 flex-shrink-0">
                          {theme.preview.map((c, i) => (
                            <div key={i} className="w-4 h-8 rounded-sm border border-gray-200" style={{ backgroundColor: c }} />
                          ))}
                        </div>
                        <div className="text-left">
                          <div className="text-xs font-semibold text-gray-800">{theme.icon} {theme.label}</div>
                          <div className="text-[9px] text-gray-400 mt-0.5">{theme.accentColor}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-[9px] text-gray-400 mt-2 px-1">Applies background and recolors text on all slides</p>
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-gray-200 mx-1" />

            {/* Background color */}
            <span className="text-[10px] text-gray-400 mr-1">Background</span>
            <div className="flex gap-0.5">
              {['#FFFFFF', '#F3F4F6', '#1F2937', '#111827', '#DBEAFE', '#FEF3C7', '#D1FAE5', '#FCE7F3'].map(c => (
                <button key={c} onClick={() => onSlideUpdate?.('bg', c)}
                  className={`w-5 h-5 rounded border ${currentSlide?.bg === c ? 'ring-2 ring-indigo-400 border-indigo-500' : 'border-gray-300'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>

            <div className="w-px h-5 bg-gray-200 mx-1" />

            {/* Slide Numbers toggle */}
            <button
              onClick={onToggleSlideNumbers}
              title={showSlideNumbers ? 'Hide slide numbers' : 'Show slide numbers'}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                showSlideNumbers
                  ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700'
                  : 'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              {/* Hash / number icon */}
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8}>
                <line x1="3" y1="5" x2="13" y2="5" />
                <line x1="3" y1="11" x2="13" y2="11" />
                <line x1="6" y1="2" x2="5" y2="14" />
                <line x1="11" y1="2" x2="10" y2="14" />
              </svg>
              Slide #s
            </button>
          </div>
        )}

        {/* ═══ DESIGN TAB — shape(s) selected ═══ */}
        {tab === 'design' && s && (
          <div className="flex items-center gap-2 flex-wrap">

            {/* ── Multi-select alignment tools ── */}
            {multiSelected && (
              <>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Align</span>

                {/* Align Left */}
                <AlignBtn title="Align Left edges" onClick={() => align('left')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="2" y="4" width="2" height="12" rx="1" />
                    <rect x="5" y="6" width="10" height="3" rx="1" />
                    <rect x="5" y="11" width="7" height="3" rx="1" />
                  </svg>
                </AlignBtn>

                {/* Align Center H */}
                <AlignBtn title="Align centers horizontally" onClick={() => align('centerH')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="9" y="2" width="2" height="16" rx="1" />
                    <rect x="4" y="6" width="12" height="3" rx="1" />
                    <rect x="5.5" y="11" width="9" height="3" rx="1" />
                  </svg>
                </AlignBtn>

                {/* Align Right */}
                <AlignBtn title="Align Right edges" onClick={() => align('right')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="16" y="4" width="2" height="12" rx="1" />
                    <rect x="5" y="6" width="10" height="3" rx="1" />
                    <rect x="8" y="11" width="7" height="3" rx="1" />
                  </svg>
                </AlignBtn>

                <div className="w-px h-5 bg-gray-200" />

                {/* Align Top */}
                <AlignBtn title="Align Top edges" onClick={() => align('top')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="4" y="2" width="12" height="2" rx="1" />
                    <rect x="6" y="5" width="3" height="10" rx="1" />
                    <rect x="11" y="5" width="3" height="7" rx="1" />
                  </svg>
                </AlignBtn>

                {/* Align Middle V */}
                <AlignBtn title="Align centers vertically" onClick={() => align('centerV')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="4" y="9" width="12" height="2" rx="1" />
                    <rect x="6" y="4" width="3" height="12" rx="1" />
                    <rect x="11" y="5.5" width="3" height="9" rx="1" />
                  </svg>
                </AlignBtn>

                {/* Align Bottom */}
                <AlignBtn title="Align Bottom edges" onClick={() => align('bottom')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="4" y="16" width="12" height="2" rx="1" />
                    <rect x="6" y="5" width="3" height="10" rx="1" />
                    <rect x="11" y="8" width="3" height="7" rx="1" />
                  </svg>
                </AlignBtn>

                <div className="w-px h-5 bg-gray-200" />

                {/* Distribute H — needs 3+ shapes */}
                <AlignBtn
                  title={canDistribute ? 'Distribute Horizontally (equal gaps)' : 'Select 3+ shapes to distribute'}
                  disabled={!canDistribute}
                  onClick={() => align('distributeH')}
                >
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="2" y="4" width="2" height="12" rx="1" />
                    <rect x="16" y="4" width="2" height="12" rx="1" />
                    <rect x="8" y="7" width="4" height="6" rx="1" />
                  </svg>
                </AlignBtn>

                {/* Distribute V — needs 3+ shapes */}
                <AlignBtn
                  title={canDistribute ? 'Distribute Vertically (equal gaps)' : 'Select 3+ shapes to distribute'}
                  disabled={!canDistribute}
                  onClick={() => align('distributeV')}
                >
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="currentColor">
                    <rect x="4" y="2" width="12" height="2" rx="1" />
                    <rect x="4" y="16" width="12" height="2" rx="1" />
                    <rect x="7" y="8" width="6" height="4" rx="1" />
                  </svg>
                </AlignBtn>

                <div className="w-px h-5 bg-gray-200" />
              </>
            )}

            {/* ── Single-shape: align to slide ── */}
            {!multiSelected && (
              <>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Center</span>
                <AlignBtn title="Center on Slide (Horizontal)" onClick={() => align('slideH')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <line x1="10" y1="2" x2="10" y2="18" strokeDasharray="2 2" />
                    <rect x="5" y="7" width="10" height="6" rx="1" />
                  </svg>
                </AlignBtn>
                <AlignBtn title="Center on Slide (Vertical)" onClick={() => align('slideV')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <line x1="2" y1="10" x2="18" y2="10" strokeDasharray="2 2" />
                    <rect x="7" y="5" width="6" height="10" rx="1" />
                  </svg>
                </AlignBtn>
                <AlignBtn title="Center on Slide (Both axes)" onClick={() => align('slideCenter')}>
                  <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <line x1="10" y1="2" x2="10" y2="18" strokeDasharray="2 2" />
                    <line x1="2" y1="10" x2="18" y2="10" strokeDasharray="2 2" />
                    <rect x="6" y="6" width="8" height="8" rx="1" />
                  </svg>
                </AlignBtn>
                <div className="w-px h-5 bg-gray-200" />
              </>
            )}

            {/* ── Rotation input ── */}
            <div className="flex items-center gap-1">
              <svg
                viewBox="0 0 16 16"
                className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path d="M13 3.5A6.5 6.5 0 1 1 8 1.5" strokeLinecap="round" />
                <path d="M13 1v3h-3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <input
                type="number"
                min="0" max="360"
                value={rotation}
                onChange={e => onUpdate(s.id, { rotation: parseInt(e.target.value) || 0 })}
                className="text-xs border border-gray-200 rounded px-1 py-0.5 w-14"
              />
              <span className="text-[10px] text-gray-400">&deg;</span>
              {rotation !== 0 && (
                <button
                  onClick={() => onUpdate(s.id, { rotation: 0 })}
                  className="text-[10px] text-indigo-500 hover:underline"
                >
                  Reset
                </button>
              )}
            </div>

            <div className="w-px h-5 bg-gray-200" />

            {/* Fill */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-gray-400 font-semibold">Fill</span>
              {FILL_COLORS.slice(0, 10).map(c => (
                <button key={c} onClick={() => onUpdate(s.id, { fill: c })}
                  className={`w-4 h-4 rounded border ${s.fill === c ? 'ring-1 ring-indigo-400 border-indigo-500' : 'border-gray-300'}`}
                  style={{ backgroundColor: c === 'transparent' ? '#fff' : c }}>
                  {c === 'transparent' && <span className="text-[8px] text-red-400 leading-none">/</span>}
                </button>
              ))}
            </div>

            {/* Opacity */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-gray-400">Opacity</span>
              <input type="range" min="0" max="100" value={Math.round((s.opacity ?? 1) * 100)}
                onChange={e => onUpdate(s.id, { opacity: parseInt(e.target.value) / 100 })}
                className="w-16 h-3 accent-indigo-500" />
              <span className="text-[10px] text-gray-500 w-7">{Math.round((s.opacity ?? 1) * 100)}%</span>
            </div>

            <div className="w-px h-5 bg-gray-200" />

            {/* Stroke */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-gray-400 font-semibold">Stroke</span>
              {STROKE_COLORS.slice(0, 6).map(c => (
                <button key={c} onClick={() => onUpdate(s.id, { stroke: c === 'transparent' ? '' : c, strokeWidth: c === 'transparent' ? 0 : (s.strokeWidth || 2) })}
                  className={`w-4 h-4 rounded border ${(s.stroke || '') === c ? 'ring-1 ring-indigo-400' : 'border-gray-300'}`}
                  style={{ backgroundColor: c === 'transparent' ? '#fff' : c }}>
                  {c === 'transparent' && <span className="text-[7px] text-red-400">/</span>}
                </button>
              ))}
              <select value={s.strokeWidth || 0} onChange={e => onUpdate(s.id, { strokeWidth: parseInt(e.target.value) })}
                className="text-[10px] border border-gray-200 rounded px-1 py-0.5 w-12">
                {[0, 1, 2, 3, 4, 5, 8, 10].map(w => <option key={w} value={w}>{w}px</option>)}
              </select>
            </div>

            <div className="w-px h-5 bg-gray-200" />

            {/* Text */}
            <div className="flex items-center gap-1">
              <select value={s.fontSize || 16} onChange={e => onUpdate(s.id, { fontSize: parseInt(e.target.value) })}
                className="text-xs border border-gray-200 rounded px-1 py-0.5 w-14">
                {FONT_SIZES.map(sz => <option key={sz} value={sz}>{sz}</option>)}
              </select>
              <button onClick={() => onUpdate(s.id, { fontWeight: s.fontWeight === 'bold' ? 'normal' : 'bold' })}
                className={`px-1.5 py-0.5 text-xs font-bold rounded border ${s.fontWeight === 'bold' ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'border-gray-200 hover:bg-gray-100'}`}>
                B
              </button>
              <button onClick={() => onUpdate(s.id, { fontStyle: s.fontStyle === 'italic' ? 'normal' : 'italic' })}
                className={`px-1.5 py-0.5 text-xs italic rounded border ${s.fontStyle === 'italic' ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'border-gray-200 hover:bg-gray-100'}`}>
                I
              </button>
              <div className="flex border border-gray-200 rounded overflow-hidden">
                {['left', 'center', 'right'].map(a => (
                  <button key={a} onClick={() => onUpdate(s.id, { textAlign: a })}
                    className={`px-1.5 py-0.5 text-[10px] ${s.textAlign === a ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-50'}`}>
                    {a === 'left' ? '≡' : a === 'center' ? '≡' : '≡'}
                  </button>
                ))}
              </div>
              <span className="text-[10px] text-gray-400">Color</span>
              <input type="color" value={s.textColor || '#333333'} onChange={e => onUpdate(s.id, { textColor: e.target.value })}
                className="w-5 h-5 rounded border border-gray-300 cursor-pointer" />
            </div>

            <div className="w-px h-5 bg-gray-200" />

            {/* Radius */}
            {s.type !== 'ellipse' && s.type !== 'text' && (
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-gray-400">Radius</span>
                <input type="number" min="0" max="100" value={s.radius || 0}
                  onChange={e => onUpdate(s.id, { radius: parseInt(e.target.value) || 0 })}
                  className="text-xs border border-gray-200 rounded px-1 py-0.5 w-12" />
              </div>
            )}

            {/* Size inputs */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-gray-400">W</span>
              <input type="number" value={Math.round(s.width)} onChange={e => onUpdate(s.id, { width: parseInt(e.target.value) || 20 })}
                className="text-xs border border-gray-200 rounded px-1 py-0.5 w-14" />
              <span className="text-[10px] text-gray-400">H</span>
              <input type="number" value={Math.round(s.height)} onChange={e => onUpdate(s.id, { height: parseInt(e.target.value) || 20 })}
                className="text-xs border border-gray-200 rounded px-1 py-0.5 w-14" />
            </div>

            <div className="w-px h-5 bg-gray-200" />

            {/* Layer */}
            <div className="flex items-center gap-0.5">
              <button onClick={() => onUpdate(s.id, { zIndex: (s.zIndex || 1) + 10 })} title="Bring forward"
                className="px-1 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100 rounded border border-gray-200">&#x2191;</button>
              <button onClick={() => onUpdate(s.id, { zIndex: Math.max(1, (s.zIndex || 1) - 10) })} title="Send backward"
                className="px-1 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100 rounded border border-gray-200">&#x2193;</button>
            </div>

            <button onClick={() => onDelete(s.id)}
              className="px-2 py-1 text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 ml-1">
              Delete
            </button>
          </div>
        )}

        {/* ═══ TRANSITIONS TAB ═══ */}
        {tab === 'transitions' && (
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Slide Transition</span>
            {[
              { value: 'none', label: 'None', icon: '—', desc: 'Instant switch' },
              { value: 'morph', label: 'Morph', icon: '◇', desc: 'Shapes animate to new positions' },
              { value: 'fade', label: 'Fade', icon: '○', desc: 'Cross-fade between slides' },
            ].map(t => (
              <button key={t.value} onClick={() => onSlideUpdate?.('transition', t.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${
                  currentSlide?.transition === t.value
                    ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}>
                <span className="text-lg w-6 text-center">{t.icon}</span>
                <div>
                  <div className="text-xs font-semibold text-gray-800">{t.label}</div>
                  <div className="text-[10px] text-gray-400">{t.desc}</div>
                </div>
              </button>
            ))}

            <div className="w-px h-8 bg-gray-200 mx-1" />

            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400">Duration</span>
              <input type="range" min="200" max="2000" step="100"
                value={currentSlide?.transitionDuration || 800}
                onChange={e => onSlideUpdate?.('transitionDuration', parseInt(e.target.value))}
                className="w-24 accent-indigo-500" />
              <span className="text-xs text-gray-500 w-10">{currentSlide?.transitionDuration || 800}ms</span>
            </div>

            <div className="w-px h-8 bg-gray-200 mx-1" />

            <button onClick={() => {
              if (!slides) return;
              const t = currentSlide?.transition || 'none';
              const d = currentSlide?.transitionDuration || 800;
              slides.forEach(sl => {
                if (sl.index > 0) { onSlideUpdate?.('transition', t, sl.id); onSlideUpdate?.('transitionDuration', d, sl.id); }
              });
            }} className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100">
              Apply to all slides
            </button>
          </div>
        )}

        {/* ═══ ANIMATIONS TAB ═══ */}
        {tab === 'animations' && (
          <div className="flex items-center gap-3">
            {s ? (
              <>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Entrance Animation</span>
                <select value={s.animation || 'none'} onChange={e => onUpdate(s.id, { animation: e.target.value })}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
                  {ANIMATION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>

                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400">Delay</span>
                  <input type="number" min="0" max="10" step="0.1" value={s.animationDelay || 0}
                    onChange={e => onUpdate(s.id, { animationDelay: parseFloat(e.target.value) || 0 })}
                    className="text-xs border border-gray-200 rounded px-1.5 py-1 w-14" />
                  <span className="text-[10px] text-gray-400">s</span>
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400">Duration</span>
                  <input type="number" min="100" max="3000" step="100" value={s.animationDuration || 500}
                    onChange={e => onUpdate(s.id, { animationDuration: parseInt(e.target.value) || 500 })}
                    className="text-xs border border-gray-200 rounded px-1.5 py-1 w-16" />
                  <span className="text-[10px] text-gray-400">ms</span>
                </div>

                <div className="w-px h-8 bg-gray-200" />

                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-400">Order</span>
                  <button onClick={() => onUpdate(s.id, { animationDelay: Math.max(0, (s.animationDelay || 0) - 0.3) })}
                    className="px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 rounded border border-gray-200">Earlier</button>
                  <button onClick={() => onUpdate(s.id, { animationDelay: (s.animationDelay || 0) + 0.3 })}
                    className="px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 rounded border border-gray-200">Later</button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">Select a shape to configure its entrance animation</span>
                {allShapes?.length > 0 && (
                  <div className="flex items-center gap-1 ml-4">
                    <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Animation Order</span>
                    {allShapes.filter(sh => sh.animation && sh.animation !== 'none').sort((a, b) => (a.animationDelay || 0) - (b.animationDelay || 0)).map((sh, i) => (
                      <span key={sh.id} className="px-2 py-1 text-[10px] bg-indigo-50 text-indigo-600 rounded border border-indigo-200 truncate max-w-[80px]" title={sh.text || sh.type}>
                        {i + 1}. {sh.text?.slice(0, 8) || sh.type}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Click-outside overlay to close dropdowns */}
      {(showLayouts || showThemes) && (
        <div className="fixed inset-0 z-40" onClick={() => { setShowLayouts(false); setShowThemes(false); }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// LayoutPreview — tiny preview of a layout
// ─────────────────────────────────────────────
function LayoutPreview({ layout }) {
  const W = 160;
  const H = 90;
  const scaleX = W / 960;
  const scaleY = H / 540;

  if (layout.shapes.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <span className="text-[9px] text-gray-400">Blank</span>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative" style={{ backgroundColor: layout.bg || '#ffffff' }}>
      {layout.shapes.map((shape, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: shape.x * scaleX,
            top: shape.y * scaleY,
            width: shape.width * scaleX,
            height: shape.height * scaleY,
            backgroundColor: shape.fill === 'transparent' ? 'transparent' : (shape.fill || '#E5E7EB'),
            borderRadius: shape.type === 'ellipse' ? '50%' : (shape.radius ? shape.radius * Math.min(scaleX, scaleY) : 2),
            border: shape.stroke ? `1px solid ${shape.stroke}` : 'none',
            opacity: shape.opacity ?? 1,
          }}
        >
          {shape.text && (
            <div style={{
              fontSize: Math.max(3, (shape.fontSize || 14) * scaleX * 0.8),
              color: shape.textColor || '#333',
              fontWeight: shape.fontWeight || 'normal',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              padding: '1px',
              lineHeight: 1.2,
            }}>
              {shape.text.split('\n')[0]}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
